'use client'
import { useMemo, useState, useRef, isValidElement, useEffect } from 'react'
import type { ChangeEvent, JSX, KeyboardEventHandler, ReactNode } from 'react'
import type { UseFormRegister as TypeRegister, FieldError, RegisterOptions } from 'react-hook-form'
import styled, { css } from 'styled-components'
import { mq, getP18InputHasContentLabel, getP18InputLabel, parsePlaceHolder, type Col, getTransition, vw, getP18 } from '@/styles'
import { Img } from '../Img'
import { motion } from 'motion/react'
import { useDeviceType } from '@/hooks'

type onChangeType = (event: ChangeEvent<HTMLInputElement>) => void

export type TypeThemeColors = 'white' | 'black'| 'background'
interface iInput {
  readonly type?: string
  readonly name: string
  readonly label?: string
  placeholder?: string
  defaultValue?: string | number | string[] | null
  readonly required?: boolean | RegisterOptions
  readonly disabled?: boolean
  readonly autoComplete?: string
  readonly readOnly?: boolean
  readonly onChange?: onChangeType
  readonly onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  readonly col?: Col
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly register?:TypeRegister<any>
  error?: FieldError | unknown
  readonly icon?: string | boolean | ReactNode
  readonly className?: string
  readonly placeholderVisible?: boolean
  readonly textColor?: TypeThemeColors
  readonly bgColor?: TypeThemeColors
  readonly autoFocus?: boolean
}

interface iLabelProps {
  $hasContent: boolean
  $hidden: boolean
  $hasError?: boolean
  $col: Col
  $placeholderVisible: boolean
  $buttonIcon: boolean
  $textColor?: TypeThemeColors
  $bgColor: TypeThemeColors
  $password?: boolean
}

const hasContentState = () => css`
  ${getP18InputHasContentLabel()}
  opacity: 1;
  transform: translateY(-30px);

`

export const variantsSpan = {
  active: { width: '100%' },
  inactive: { width: '0%' },
}

const mediaPasswordIcon1 = {
  type: 'svg' as const,
  alt: 'Password eye icon',
  src: '/images/svg/i--hide-password.svg',
  size: { width: 18, height: 18 }
}
const mediaPasswordIcon2 = {
  type: 'svg' as const,
  alt: 'Password eye icon',
  src: '/images/svg/i--show-password.svg',
  size: { width: 18, height: 18 }
}

// aria-invalid https://react-hook-form.com/advanced-usage
const getIcon = (icon, type, setInputType, setIconType, iconType) => {
  if(!icon) return false
  if(typeof icon === 'boolean' || typeof icon === 'string') {
    const mediaIcon = iconType === 'password' ? mediaPasswordIcon2 : mediaPasswordIcon1
    if(type === 'password') {
      const Icon = <button key='icon' type='button' className='btn--toggle-password' onClick={(e) => {
        e.preventDefault()
        setInputType(prevType => prevType === 'password' ? 'text' : 'password')
        setIconType(prevType => prevType === 'password' ? 'text' : 'password')
      }}><Img media={mediaIcon}/></button>
      return [Icon, true]
    } else if(type === 'search') {
      // Search and clear action
    }
  } else if (isValidElement(icon)) {

  }
}

export const transition = {
  ...getTransition(),
  type: 'tween',
} as const


export const noRegister = () => ({ onChange: null, onBlur: null, ref: null, name: null })
// Attributes https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input

export const Input = ({ type = 'text', name = '', defaultValue = '', label, autoComplete, placeholder, placeholderVisible = false, required = false, disabled = false, readOnly = null, onChange = null, col, register = noRegister, icon, className, textColor = 'black', bgColor = 'background', error, autoFocus = false, onKeyDown }:iInput):JSX.Element => {
  const requireProps:RegisterOptions = typeof required === 'boolean' ? { required } : required
  const { onChange:registerOnChange, onBlur:registerOnBlur, name:registerName, ref:registerRef } = register(name, requireProps)
  const ref = useRef<HTMLInputElement | null>(null)

  const [inputType, setInputType] = useState(type)
  const [inputValue, setInputValue] = useState(defaultValue || '')
  const [iconType, setIconType] = useState('password')
  const isPasswrord = useMemo(() => type === 'password' && iconType === 'password', [type, iconType])
  const { isMobile } = useDeviceType()
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const handleChange = e => {
    registerOnChange?.(e)
    onChange?.(e)
    setInputValue(e?.target?.value)
  }

  const handleBlur = e => {
    setIsFocused(false)
    registerOnBlur?.(e)
    setInputValue(e?.target?.value)
  }

  const ButtonIcon = useMemo(() => getIcon(icon, type, setInputType, setIconType, iconType), [icon, type, setIconType, iconType])

  useEffect(() => {
    if(error) console.warn(`input ${name} error`, error)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  useEffect(() => {
    if (autoFocus && ref.current) ref.current.focus()
  }, [autoFocus])


  return (
    <Label $hasContent={!!inputValue} $hidden={inputType === 'hidden'} $col={col} $buttonIcon={!!ButtonIcon} $placeholderVisible={placeholderVisible} $textColor={textColor} $bgColor={bgColor} $hasError={!!error} $password={isPasswrord}{...className && { className }}>
      <input type={inputType} name={registerName ?? name} disabled={disabled} readOnly={readOnly} placeholder={placeholder ?? label} onChange={handleChange} onBlur={handleBlur} onFocus={() => setIsFocused(true)} aria-invalid={!!error} value={inputValue} required={typeof required === 'boolean' ? required : undefined} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} autoComplete={autoComplete} onKeyDown={onKeyDown}
        ref={(e) => {
          ref.current = e
          registerRef?.(e)
        }}
      />
      <Span className='hover' custom={isMobile} transition={transition} variants={variantsSpan} initial='inactive' animate={!disabled && (isHovered || isFocused ) ? 'active' : 'inactive'}
      />
      {label && <span>{label}</span>}
      {ButtonIcon && ButtonIcon}
      {(error as FieldError)?.message && <span role='alert'>{(error as FieldError).message}</span>}
    </Label>
  )
}

const Label = styled.label<iLabelProps>`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  padding:0;
  pointer-events: all;
  position: relative;
  width: 100%;

  ${mq.greaterThan('tablet') } {
    padding: 0;
    width: ${({ $col }) => typeof $col === 'number' ? `${100 * $col / 12}%` : $col };
  }

  ${({ $hidden }) => $hidden && css`
    opacity: 0;
    pointer-events: none;
    position: absolute;
    user-select: none;
    visibility: hidden;
  `}

  > input {
    ${getP18()}
    background-color: transparent;
    border-bottom: 1px solid;
    border-color: ${({ $hasError, $hasContent }) => $hasError ? '#FF3945' : $hasContent ? 'rgba(0, 0, 0, .2)': 'rgba(0, 0, 0, .2)'};
    color: var(--color-text);
    font-size: 18px;
    font-weight:400;
    padding-bottom: ${vw(9.5, 'mobile')};
    padding-right: ${vw(20, 'mobile')};
    /* height: 60px; */
    letter-spacing: ${({ $password }) => $password  ? '5px' : '-.3px'};
    line-height: normal;
    /* padding: 25px 0 12px; */
    transition: 300ms border-color ease-out;
    width: 100%;

    ${mq.greaterThan('tablet')}{
      padding-bottom: ${vw(10, 'desktop')};
      padding-right: 0;
    }

    &[readonly],
    &[disabled] {
      cursor: auto;
      pointer-events: none;
    }

    &[readonly] {
      opacity: .3;

      + span {
        opacity: .3;
      }
    }

    &[disabled] {
      opacity: .2;

      + span {
        opacity: .2;
      }
    }


    &:hover {
      border-color: ${({ $hasError }) => $hasError ? '#FF3945' : 'var(--color-yellow)'};
    }
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      -webkit-text-fill-color: var(--color-black);
      -webkit-box-shadow: 0 0 0 500px var(--color-background) inset;
      box-shadow: 0 0 0 100px var(--color-background) inset;
      color: inherit;
    }

    ${({ $placeholderVisible }) => parsePlaceHolder(`
      opacity: ${$placeholderVisible ? .4 : 0};
      color: var(--color-text);
    `)}
  }

  > span:not(.hover) {
    /* FLOATING LABEL */
    &:first-of-type {
      ${({ $placeholderVisible }) => $placeholderVisible ? hasContentState() : getP18InputLabel()}
      border-bottom: 1px solid rgba(0, 0, 0, 0);
      bottom: 12px;
      color: var(--color-black);
      opacity: 1;
      position: absolute;
      transition: 300ms font-size ease, 300ms transform ease, 300ms opacity ease;
      white-space: nowrap;

      ${({ $hasContent }) => $hasContent ? css`${hasContentState()}` : ''}
    }

    /* ERROR MESSAGE */
    &[role='alert'] {
      color: #FF3945;
      font-size: 10px;
      font-weight: 400;
      pointer-events: none;
      position: absolute;
      top: 35px;

      ${mq.greaterThan('tablet')} {
        top: 28px;
      }
    }
  }

  figure {
    height: 22px;
    position: absolute;
    right: 17px;
    top: 40%;
    width: 30px;


    img {
      height: 100%;
      width: 100%;
    }
  }

  .btn--toggle-password {
    align-items: center;
    background: transparent;
    border-bottom: 1px solid rgba(0, 0, 0, 0);
    top: 50%;
    display: flex;
    height: 21px;
    position: absolute;
    right: 4.25px;
    user-select: none;
    transform: translateY(-50%);

    ${mq.greaterThan('tablet')} {
      height: 21px;
      right: 12.5px;
    }
  }
`


export const Span = styled(motion.span)<{ $error?: boolean }>`
  position: absolute;
  bottom: 0;
  height: 1px;
  background-color: ${({ $error }) => $error ? 'var(--color-input-error)': 'var(--color-black)'};
  transition: background-color 300ms var(--transition-ease);
  left: 0;
  width: 0%;
`
